import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { AlertCircle, CheckCircle, ChevronRight } from "lucide-react";
import { API, GARMENT_TYPES } from "../constants";

const CustomerDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [customerEmail, setCustomerEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [workflows, setWorkflows] = useState({});
  const [showOrderDetails, setShowOrderDetails] = useState(false);

  // Enhanced product name mapping
  const PRODUCT_NAMES = {
    'dtf_printing': 'DTF (Direct-to-Film) Printing',
    'screen_printing': 'Screen Printing', 
    'embroidery': 'Custom Embroidery',
    'stickers': 'Custom Stickers',
    'large_format': 'Large Format Printing',
    'fastisol_transfers': 'Fastisol Transfers'
  };

  useEffect(() => {
    fetchWorkflows();
  }, []);

  const fetchWorkflows = async () => {
    try {
      const response = await axios.get(`${API}/workflows`);
      setWorkflows(response.data);
    } catch (error) {
      console.error('Error fetching workflows:', error);
    }
  };

  const fetchOrders = async (email) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/customers/${email}/orders`);
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      toast.error('Failed to fetch orders. Please check your email address.');
    }
    setLoading(false);
  };

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    if (customerEmail) {
      fetchOrders(customerEmail);
    }
  };

  const getWorkflowSteps = (workflowType) => {
    return workflows[workflowType]?.steps || [];
  };

  const getStatusIndex = (status, workflowType) => {
    const steps = getWorkflowSteps(workflowType);
    return steps.findIndex(step => step.status === status);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (selectedOrder) {
    const workflow = getWorkflowSteps(selectedOrder.workflow_type);
    const currentIndex = getStatusIndex(selectedOrder.status, selectedOrder.workflow_type);
    
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="flex items-center mb-6">
          <Button 
            variant="outline" 
            onClick={() => setSelectedOrder(null)}
            className="mr-4"
          >
            ← Back to Orders
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">
            Order #{selectedOrder.id.slice(-8).toUpperCase()}
          </h1>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle>Order Progress</CardTitle>
                <CardDescription>
                  {workflows[selectedOrder.workflow_type]?.name} - {selectedOrder.items.length} item(s)
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {workflow.map((step, index) => (
                    <div key={step.status} className="flex items-start space-x-4">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                        index <= currentIndex
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {index <= currentIndex ? '✓' : index + 1}
                      </div>
                      <div className="flex-1">
                        <h4 className={`font-semibold ${
                          index <= currentIndex ? 'text-green-700' : 'text-gray-600'
                        }`}>
                          {step.name}
                        </h4>
                        <p className="text-sm text-gray-600">{step.description}</p>
                        {index === currentIndex && (
                          <Badge className="mt-1 bg-blue-100 text-blue-800">Current Status</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                
                {selectedOrder.tracking_number && (
                  <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                    <h4 className="font-semibold text-green-800 mb-2">Tracking Information</h4>
                    <p className="text-green-700">Tracking Number: <span className="font-mono">{selectedOrder.tracking_number}</span></p>
                  </div>
                )}

                {selectedOrder.notes && (
                  <div className="mt-4 p-4 bg-gray-50 border rounded-lg">
                    <h4 className="font-semibold text-gray-800 mb-2">Notes</h4>
                    <p className="text-gray-700">{selectedOrder.notes}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div>
            <Card>
              <CardHeader>
                <CardTitle>Order Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-sm font-semibold">Customer</Label>
                  <p className="text-sm">{selectedOrder.customer_name}</p>
                  <p className="text-sm text-gray-600">{selectedOrder.customer_email}</p>
                </div>
                
                <div>
                  <Label className="text-sm font-semibold">Order Date</Label>
                  <p className="text-sm">{formatDate(selectedOrder.created_at)}</p>
                </div>

                <div>
                  <Label className="text-sm font-semibold">Items</Label>
                  <div className="space-y-2">
                    {selectedOrder.items.map((item, index) => (
                      <div key={index} className="text-sm p-2 bg-gray-50 rounded">
                        <div className="font-medium">{PRODUCT_NAMES[item.product_id] || item.product_id}</div>
                        <div className="text-gray-600">Qty: {item.quantity}</div>
                        {item.garment_type && (
                          <div className="text-gray-600">
                            {GARMENT_TYPES[item.garment_type]?.name}
                          </div>
                        )}
                        <div className="font-medium">${item.price}</div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t pt-4 space-y-3">
                  <div className="flex justify-between">
                    <span className="font-semibold">Total</span>
                    <span className="font-semibold">${selectedOrder.total || selectedOrder.total_amount}</span>
                  </div>
                  
                  {/* Payment Status */}
                  <div className="flex justify-between items-center">
                    <span className="text-sm font-medium">Payment Status</span>
                    {selectedOrder.payment_intent_id ? (
                      <Badge className="bg-green-100 text-green-800">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Payment Confirmed
                      </Badge>
                    ) : (
                      <Badge className="bg-orange-100 text-orange-800">
                        <AlertCircle className="w-3 h-3 mr-1" />
                        Processing Payment
                      </Badge>
                    )}
                  </div>
                  
                  {selectedOrder.payment_intent_id && (
                    <div className="text-xs text-gray-500">
                      Payment ID: {selectedOrder.payment_intent_id.slice(-8)}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <h1 className="text-4xl font-bold text-gray-900 mb-8">Customer Dashboard</h1>
      
      {orders.length === 0 ? (
        <Card className="p-8">
          <CardContent className="text-center">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">Track Your Orders</h2>
            <p className="text-gray-600 mb-6">
              Enter your email address to view your order history and track progress.
            </p>
            <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
              <div className="flex gap-2">
                <Input
                  type="email"
                  placeholder="Enter your email address"
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  required
                />
                <Button type="submit" disabled={loading}>
                  {loading ? 'Searching...' : 'Track Orders'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      ) : (
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-semibold">Your Orders ({orders.length})</h2>
            <Button variant="outline" onClick={() => {setOrders([]); setCustomerEmail('');}}>
              Search Different Email
            </Button>
          </div>
          
          <div className="grid gap-4">
            {orders.map((order) => {
              const workflow = getWorkflowSteps(order.workflow_type);
              const currentIndex = getStatusIndex(order.status, order.workflow_type);
              const currentStep = workflow[currentIndex];
              
              return (
                <Card key={order.id} className="cursor-pointer hover:shadow-md transition-shadow">
                  <CardContent className="p-6" onClick={() => setSelectedOrder(order)}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-4 mb-2">
                          <h3 className="text-lg font-semibold">
                            Order #{order.id.slice(-8).toUpperCase()}
                          </h3>
                          <Badge variant="secondary">{workflows[order.workflow_type]?.name}</Badge>
                          {order.payment_intent_id ? (
                            <Badge className="bg-green-100 text-green-800">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Paid
                            </Badge>
                          ) : (
                            <Badge className="bg-orange-100 text-orange-800">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </div>
                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Status: </span>
                            <span className="font-medium">{currentStep?.name || order.status}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Items: </span>
                            <span className="font-medium">{order.items.length}</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Total: </span>
                            <span className="font-medium">${order.total || order.total_amount}</span>
                          </div>
                        </div>
                        <div className="text-sm text-gray-500 mt-2">
                          Ordered {formatDate(order.created_at)}
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Thank You Page Component  

export { CustomerDashboard };
export default CustomerDashboard;
